import {
  ServiceTypeFacet,
  ServiceTypeFacetSaveDTO,
  useCreateServiceTypeFacetMutation,
  useDeleteServiceTypeFacetMutation,
  useUpdateServiceTypeFacetMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseServiceTypeFacetMutationProps = {}

export type UseServiceTypeFacetMutationResult = {
  createServiceTypeFacet: (serviceTypeFacet: ServiceTypeFacetSaveDTO, callback: EntityMutationCallback<ServiceTypeFacet> | undefined) => void
  updateServiceTypeFacet: (serviceTypeFacet: ServiceTypeFacetSaveDTO, callback: EntityMutationCallback<ServiceTypeFacet> | undefined) => void
  deleteServiceTypeFacet: (serviceTypeFacetId: string, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useServiceTypeFacetMutation = (props: UseServiceTypeFacetMutationProps = {}): UseServiceTypeFacetMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createServiceTypeFacet, {isLoading: isCreating}] = useCreateServiceTypeFacetMutation()
  const [_updateServiceTypeFacet, {isLoading: isUpdating}] = useUpdateServiceTypeFacetMutation()
  const [_deleteServiceTypeFacet, {isLoading: isDeleting}] = useDeleteServiceTypeFacetMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useServiceTypeFacetUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.servicetypefacet", "Service Type Facet"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceTypeFacetUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useServiceTypeFacetUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.servicetypefacet", "Service Type Facet"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceTypeFacetUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useServiceTypeFacetUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.servicetypefacet", "Service Type Facet"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceTypeFacetUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ServiceTypeFacet>, callback: EntityMutationCallback<ServiceTypeFacet> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.servicetypefacet", "Service Type Facet") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback<ServiceTypeFacet> | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.servicetypefacet", "Service Type Facet") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.servicetypefacet", "Service Type Facet") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((serviceTypeFacetId: string, callback: EntityMutationCallback<ServiceTypeFacet> | undefined) => {
    const promise = _deleteServiceTypeFacet({id: serviceTypeFacetId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteServiceTypeFacet, handleDeleteResult])

  const createServiceTypeFacet = useCallback((serviceTypeFacet: ServiceTypeFacetSaveDTO, callback: EntityMutationCallback<ServiceTypeFacet> | undefined) => {
    const promise = _createServiceTypeFacet(serviceTypeFacet).unwrap()
    handleCreateResult(promise, callback)
  }, [_createServiceTypeFacet, handleCreateResult])

  const updateServiceTypeFacet = useCallback((serviceTypeFacet: ServiceTypeFacetSaveDTO, callback: EntityMutationCallback | undefined) => {
    const promise = _updateServiceTypeFacet(serviceTypeFacet).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateServiceTypeFacet, handleUpdateResult])

  const deleteServiceTypeFacet = useCallback((serviceTypeFacetId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.servicetypefacet", "Service Type Facet"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.servicetypefacet.confirm", "Are you sure you want to delete this service type facet?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(serviceTypeFacetId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createServiceTypeFacet: createServiceTypeFacet,
    updateServiceTypeFacet: updateServiceTypeFacet,
    deleteServiceTypeFacet: deleteServiceTypeFacet,

    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
